import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { Mail, Lock, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import MFAVerification from '@/components/MFAVerification';
import { AuthLayout, FIELD_WITH_ICON_CLASS } from '@/components/landing/page-kit';

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, mfaPending, completeMfa, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('clevia-remember-me') !== 'false'
  );

  // If user is authenticated with MFA pending (e.g. OAuth + 2FA), show MFA screen
  const showMFAScreen = showMFA || (isAuthenticated && mfaPending);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    localStorage.setItem('clevia-remember-me', rememberMe ? 'true' : 'false');
    try {
      await login(data);

      // Check if MFA is required (with timeout — don't block login if MFA service is slow)
      try {
        const mfaTimeout = <T,>(p: Promise<T>) =>
          Promise.race([
            p,
            new Promise<never>((_, rej) => setTimeout(() => rej(new Error('mfa timeout')), 3000)),
          ]);

        const { data: factorsData } = await mfaTimeout(supabase.auth.mfa.listFactors());
        const verifiedFactors = factorsData?.totp.filter((f) => f.status === 'verified') || [];

        if (verifiedFactors.length > 0) {
          const { data: aalData } = await mfaTimeout(
            supabase.auth.mfa.getAuthenticatorAssuranceLevel()
          );

          if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') {
            setShowMFA(true);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // MFA check failed or timed out — continue without MFA
      }

      toast({
        title: 'Connexion réussie',
        description: 'Bienvenue sur clevia !',
      });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Erreur de connexion',
        description: error instanceof Error ? error.message : 'Email ou mot de passe incorrect',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASuccess = () => {
    completeMfa();
    setShowMFA(false);
    toast({
      title: 'Connexion réussie',
      description: 'Bienvenue sur clevia !',
    });
    navigate('/dashboard');
  };

  const handleMFACancel = async () => {
    completeMfa();
    await supabase.auth.signOut();
    setShowMFA(false);
  };

  return (
    <AuthLayout
      back={{ to: '/', label: "Retour à l'accueil" }}
      title={showMFAScreen ? 'Vérification 2FA' : 'Bon retour !'}
      subtitle={
        showMFAScreen
          ? "Entrez le code de votre application d'authentification"
          : 'Connectez-vous pour accéder à votre tableau de bord'
      }
      footer={
        !showMFAScreen ? (
          <p className="text-muted-foreground">
            Pas encore de compte ?{' '}
            <Link
              to="/register"
              className="font-medium text-brand-700 transition-opacity hover:opacity-80 dark:text-brand-400"
            >
              S'inscrire gratuitement
            </Link>
          </p>
        ) : undefined
      }
      aside={
        <>
          <Lock className="h-4 w-4" aria-hidden="true" />
          <span>Connexion sécurisée et cryptée</span>
        </>
      }
    >
      {showMFAScreen ? (
        <MFAVerification onSuccess={handleMFASuccess} onCancel={handleMFACancel} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email
            </Label>
            <div className="group relative">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" />
              <Input
                id="email"
                type="email"
                placeholder="nom@exemple.com"
                {...register('email')}
                disabled={isLoading}
                className={FIELD_WITH_ICON_CLASS}
              />
            </div>
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              Mot de passe
            </Label>
            <div className="group relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                {...register('password')}
                disabled={isLoading}
                className={FIELD_WITH_ICON_CLASS}
              />
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => setRememberMe(!rememberMe)}
                className="group flex items-center gap-2"
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border transition-colors duration-200 ${
                    rememberMe
                      ? 'border-primary bg-primary'
                      : 'border-border group-hover:border-primary/50'
                  }`}
                >
                  {rememberMe && (
                    <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
                  )}
                </span>
                <span className="text-xs text-muted-foreground">{t('auth.login.rememberMe')}</span>
              </button>
              <Link
                to="/forgot-password"
                className="ml-4 whitespace-nowrap text-xs text-brand-700 transition-opacity hover:opacity-80 dark:text-brand-400"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="h-11 w-full shadow-lg shadow-brand-600/20 transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" />
                Connexion...
              </span>
            ) : (
              'Se connecter'
            )}
          </Button>

          <div className="flex items-center gap-3 pt-1">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">ou</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              localStorage.setItem('clevia-remember-me', rememberMe ? 'true' : 'false');
              await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: { redirectTo: window.location.origin },
              });
            }}
            disabled={isLoading}
            className="h-11 w-full gap-3 font-medium"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continuer avec Google
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
